import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";

import "./styles.scss";
import { buscarCidades } from "../../Municipios/services/buscarCidades";
import { buscarLocaisAtendimento } from "../../LocaisAtendimento/services/buscarLocaisAtendimento";
import Container from "../../../componentes/Container";
import { BotaoComTextoEIcone, Cartao } from "@digix-ui/componentes";
import { CampoDeTexto } from "../../../componentes";

type FormPeriodo = { inicio: string; fim: string };
type FormCidade = { cidadeId: string; inicio: string; fim: string };
type FormLocal = { localId: string; inicio: string; fim: string };

const mascaraDataBR = (valor: string) => {
  const v = valor.replace(/\D/g, "").slice(0, 8);
  const d = v.slice(0, 2);
  const m = v.slice(2, 4);
  const a = v.slice(4, 8);
  if (v.length <= 2) return d;
  if (v.length <= 4) return `${d}/${m}`;
  return `${d}/${m}/${a}`;
};

const RelatoriosAtendimentos: React.FC = () => {
  const { data: cidades, isLoading: loadingCidades } = useQuery({
    queryKey: ["cidades"],
    queryFn: buscarCidades,
  });

  const { data: locaisResp, isLoading: loadingLocais } = useQuery({
    queryKey: ["locais-atendimento-combo"],
    queryFn: () =>
      buscarLocaisAtendimento({
        descricao: "",
        idCidade: "",
        paginaAtual: 1,
      }),
  });
  const locais: { id: number; descricao: string }[] =
    locaisResp?.modelo?.map((l: any) => ({
      id: l.id,
      descricao: l.descricao,
    })) ?? [];

  const {
    control: ctrlPlanilha,
    handleSubmit: handlePlanilha,
    watch: watchPlanilha,
  } = useForm<FormLocal>({
    defaultValues: { localId: "", inicio: "", fim: "" },
    mode: "onChange",
  });
  const wPlan = watchPlanilha();
  const podePlanilha =
    wPlan.localId && wPlan.inicio.length === 10 && wPlan.fim.length === 10;

  // 2) Quantidade por tipo de serviço (cidade + período)
  const {
    control: ctrlPorServico,
    handleSubmit: handlePorServico,
    watch: watchServico,
  } = useForm<FormCidade>({
    defaultValues: { cidadeId: "", inicio: "", fim: "" },
    mode: "onChange",
  });
  const wServ = watchServico();
  const podePorServico =
    wServ.cidadeId && wServ.inicio.length === 10 && wServ.fim.length === 10;

  // 3) Quantidade por atualização cadastral (período)
  const {
    control: ctrlAtualizacao,
    handleSubmit: handleAtualizacao,
    watch: watchAtual,
  } = useForm<FormPeriodo>({
    defaultValues: { inicio: "", fim: "" },
    mode: "onChange",
  });
  const wAtual = watchAtual();
  const podeAtualizacao =
    wAtual.inicio.length === 10 && wAtual.fim.length === 10;

  // 4) Pretendentes agendados e atendidos (local + período)
  const {
    control: ctrlAgendados,
    handleSubmit: handleAgendados,
    watch: watchAg,
  } = useForm<FormLocal>({
    defaultValues: { localId: "", inicio: "", fim: "" },
    mode: "onChange",
  });
  const wAg = watchAg();
  const podeAgendados =
    wAg.localId && wAg.inicio.length === 10 && wAg.fim.length === 10;

  // rotas — ajuste se necessário
  const abrirPlanilha = ({ localId, inicio, fim }: FormLocal) => {
    const qs = new URLSearchParams({ localId, inicio, fim });
    window.open(`/relatorios/planilha-de-inscritos?${qs}`, "_blank");
  };
  const abrirPorServico = ({ cidadeId, inicio, fim }: FormCidade) => {
    const qs = new URLSearchParams({ idCidade: cidadeId, inicio, fim });
    window.open(`/relatorios/atendimentos-por-tipo-servico?${qs}`, "_blank");
  };
  const abrirAtualizacao = ({ inicio, fim }: FormPeriodo) => {
    const qs = new URLSearchParams({ inicio, fim });
    window.open(
      `/relatorios/atendimentos-por-atualizacao-cadastral?${qs}`,
      "_blank"
    );
  };
  const abrirAgendados = ({ localId, inicio, fim }: FormLocal) => {
    const qs = new URLSearchParams({ localId, inicio, fim });
    window.open(`/relatorios/agendados-e-atendidos?${qs}`, "_blank");
  };

  return (
    <Container comFundo tamanhoDoContainer="grande" comCabecalhoFixo>
      <div className="relatorios-grid">
        <Cartao
          cor="secundaria"
          icone="fal fa-file-spreadsheet"
          titulo={
            <span className="u-texto-negrito">Planilha de inscritos</span>
          }
        >
          <p className="relatorio-descricao">
            Esse relatório informa quantas inscrições foram realizadas,
            separadas por cidade e em determinado período. Além disso, as
            categoriza pelo status em que se encontram.
          </p>

          <form
            className="relatorio-form"
            onSubmit={handlePlanilha(abrirPlanilha)}
          >
            <div className="campo">
              <label className="campo__rotulo">
                Selecione um local de atendimento
              </label>
              <Controller
                name="localId"
                control={ctrlPlanilha}
                rules={{ required: true }}
                render={({ field }) => (
                  <select
                    {...field}
                    className="campo__entrada"
                    disabled={loadingLocais}
                  >
                    <option value="">Selecionar</option>
                    {locais.map((l) => (
                      <option key={l.id} value={String(l.id)}>
                        {l.descricao}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className="campo">
              <label className="campo__rotulo">Defina um período</label>
              <div className="relatorio-form__periodo">
                <Controller
                  name="inicio"
                  control={ctrlPlanilha}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
                <span className="relatorio-form__ate">até</span>
                <Controller
                  name="fim"
                  control={ctrlPlanilha}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
              </div>
            </div>

            <div className="relatorio-form__acoes">
              <BotaoComTextoEIcone
                tipo="submit"
                cor="secundaria"
                tamanho="pequeno"
                icone="far fa-file-alt"
                posicaoDoIcone="esquerda"
                habilitado={!!podePlanilha}
                classesAuxiliares={`botao-relatorio ${
                  podePlanilha ? "" : "is-desabilitado"
                }`}
              >
                Gerar relatório
              </BotaoComTextoEIcone>
            </div>
          </form>
        </Cartao>

        <Cartao
          cor="secundaria"
          icone="fal fa-headset"
          titulo={
            <span className="u-texto-negrito">
              Quantidade de atendimentos por tipo de serviço
            </span>
          }
        >
          <p className="relatorio-descricao">
            Esse relatório informa quais são os pretendentes inscritos,
            separados por cidade, com período definido.
          </p>

          <form
            className="relatorio-form"
            onSubmit={handlePorServico(abrirPorServico)}
          >
            <div className="campo">
              <label className="campo__rotulo">Selecione uma cidade</label>
              <Controller
                name="cidadeId"
                control={ctrlPorServico}
                rules={{ required: true }}
                render={({ field }) => (
                  <select
                    {...field}
                    className="campo__entrada"
                    disabled={loadingCidades}
                  >
                    <option value="">Selecionar</option>
                    {cidades?.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className="campo">
              <label className="campo__rotulo">Defina um período</label>
              <div className="relatorio-form__periodo">
                <Controller
                  name="inicio"
                  control={ctrlPorServico}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
                <span className="relatorio-form__ate">até</span>
                <Controller
                  name="fim"
                  control={ctrlPorServico}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
              </div>
            </div>

            <div className="relatorio-form__acoes">
              <BotaoComTextoEIcone
                tipo="submit"
                cor="secundaria"
                tamanho="pequeno"
                icone="far fa-file-alt"
                posicaoDoIcone="esquerda"
                habilitado={!!podePorServico}
                classesAuxiliares={`botao-relatorio ${
                  podePorServico ? "" : "is-desabilitado"
                }`}
              >
                Gerar relatório
              </BotaoComTextoEIcone>
            </div>
          </form>
        </Cartao>

        <Cartao
          cor="secundaria"
          icone="fal fa-address-card"
          titulo={
            <span className="u-texto-negrito">
              Quantidade de atendimentos por atualização cadastral
            </span>
          }
        >
          <p className="relatorio-descricao">
            Versão detalhada por cidade com foco em atualizações cadastrais
            dentro do período.
          </p>

          <form
            className="relatorio-form"
            onSubmit={handleAtualizacao(abrirAtualizacao)}
          >
            <div className="campo">
              <label className="campo__rotulo">Defina um período</label>
              <div className="relatorio-form__periodo">
                <Controller
                  name="inicio"
                  control={ctrlAtualizacao}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
                <span className="relatorio-form__ate">até</span>
                <Controller
                  name="fim"
                  control={ctrlAtualizacao}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
              </div>
            </div>

            <div className="relatorio-form__acoes">
              <BotaoComTextoEIcone
                tipo="submit"
                cor="secundaria"
                tamanho="pequeno"
                icone="far fa-file-alt"
                posicaoDoIcone="esquerda"
                habilitado={!!podeAtualizacao}
                classesAuxiliares={`botao-relatorio ${
                  podeAtualizacao ? "" : "is-desabilitado"
                }`}
              >
                Gerar relatório
              </BotaoComTextoEIcone>
            </div>
          </form>
        </Cartao>

        <Cartao
          cor="secundaria"
          icone="fal fa-calendar-alt"
          titulo={
            <span className="u-texto-negrito">
              Pretendentes agendados e atendidos
            </span>
          }
        >
          <p className="relatorio-descricao">
            Quantitativo por status, local de atendimento e período selecionado.
          </p>

          <form
            className="relatorio-form"
            onSubmit={handleAgendados(abrirAgendados)}
          >
            <div className="campo">
              <label className="campo__rotulo">
                Selecione um local de atendimento
              </label>
              <Controller
                name="localId"
                control={ctrlAgendados}
                rules={{ required: true }}
                render={({ field }) => (
                  <select
                    {...field}
                    className="campo__entrada"
                    disabled={loadingLocais}
                  >
                    <option value="">Selecionar</option>
                    {locais.map((l) => (
                      <option key={l.id} value={String(l.id)}>
                        {l.descricao}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className="campo">
              <label className="campo__rotulo">Defina um período</label>
              <div className="relatorio-form__periodo">
                <Controller
                  name="inicio"
                  control={ctrlAgendados}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
                <span className="relatorio-form__ate">até</span>
                <Controller
                  name="fim"
                  control={ctrlAgendados}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
              </div>
            </div>

            <div className="relatorio-form__acoes">
              <BotaoComTextoEIcone
                tipo="submit"
                cor="secundaria"
                tamanho="pequeno"
                icone="far fa-file-alt"
                posicaoDoIcone="esquerda"
                habilitado={!!podeAgendados}
                classesAuxiliares={`botao-relatorio ${
                  podeAgendados ? "" : "is-desabilitado"
                }`}
              >
                Gerar relatório
              </BotaoComTextoEIcone>
            </div>
          </form>
        </Cartao>
      </div>
    </Container>
  );
};

export default RelatoriosAtendimentos;
